import { useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { PREFERRED_SIGNUPS, formatKickoff, hasStarted, mapsUrl, memberName, readImage } from './logic'
import PullToRefresh from '../../components/PullToRefresh/PullToRefresh'
import { findTeam, useTeamState } from './store'
import kampStand from '../../images/hold-kamp-stand.jpg'
import kampKick from '../../images/hold-kamp-kick.jpg'
import kampParken from '../../images/hold-kamp-parken.jpg'
import FilterRoundedIcon from '@mui/icons-material/FilterRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import IconButton from '../../components/IconButton/IconButton'

const mapsDialogPaper = {
  className: 'hold-create-paper',
  sx: {
    backgroundColor: '#221c24',
    color: '#f3eadf',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '18px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
  },
}

const mapsDialogBackdrop = {
  sx: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
}

const MATCH_BACKDROPS = [kampStand, kampKick, kampParken]

export function matchBackdrop(matches, match) {
  const ordered = [...matches].sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff))
  const index = ordered.findIndex((item) => item.id === match.id)
  return MATCH_BACKDROPS[(index < 0 ? 0 : index) % MATCH_BACKDROPS.length]
}

export function HoldScreen({ children, className = '' }) {
  const { state, refresh } = useTeamState()
  return (
    <PullToRefresh onRefresh={refresh}>
      <main className={`hold-screen${className ? ` ${className}` : ''}`}>
        <HoldBackdrop />
        <HoldNotices />
        {state.error ? <p className="hold-note">{state.error}</p> : null}
        {children}
      </main>
    </PullToRefresh>
  )
}

function HoldBackdrop() {
  const { teamId } = useParams()
  const { state } = useTeamState()
  const team = findTeam(state, teamId)
  if (!team?.background) return null
  return <div className="hold-backdrop" style={{ backgroundImage: `url(${team.background})` }} aria-hidden="true" />
}

export function TeamMark({ team, className }) {
  if (team.logo) {
    return <img className={className} src={team.logo} alt="" />
  }
  return <span className={className} aria-hidden="true">{team.initials}</span>
}

function HoldNotices() {
  const { teamId } = useParams()
  const { state, dispatch } = useTeamState()
  const notices = (state.notices || []).filter((notice) => !teamId || notice.teamId === teamId)
  if (notices.length === 0) return null

  return (
    <div className="hold-notices">
      {notices.map((notice) => {
        const team = state.teams.find((item) => item.id === notice.teamId)
        const author = memberName(team?.members.find((member) => member.id === notice.authorId), 'Dig')
        const kicker = notice.kind === 'match' ? 'Kamp' : 'Væg'
        const title = notice.kind === 'match'
          ? `${author} skrev på ${notice.matchLabel}`
          : `${author} skrev på væggen`
        return (
          <div key={notice.id} className="hold-reminder hold-notice">
            <Link to={notice.path} onClick={() => dispatch({ type: 'DISMISS_NOTICE', id: notice.id })}>
              <span className="hold-reminder-kicker">{kicker}</span>
              <strong>{title}</strong>
              <span className="hold-meta">{notice.text || 'Sendte et billede'}</span>
            </Link>
            <button type="button" aria-label="Luk notifikation" onClick={() => dispatch({ type: 'DISMISS_NOTICE', id: notice.id })}>
              Luk
            </button>
          </div>
        )
      })}
    </div>
  )
}

export function SeasonSwitch({ team, season, dispatch }) {
  if (team.seasons.length < 2) return null
  return (
    <div className="hold-seasons" role="tablist" aria-label="Sæson">
      {team.seasons.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={item.id === season.id}
          className={item.id === season.id ? 'is-selected' : ''}
          onClick={() => dispatch({ type: 'SET_VIEWING', teamId: team.id, seasonId: item.id })}
        >
          {item.name}
        </button>
      ))}
    </div>
  )
}

export function ArchiveNote({ season }) {
  if (season.current) return null
  return (
    <p className="hold-note">
      Tidligere sæson. Du kan læse med, men ikke melde dig til.
    </p>
  )
}

export function PlaceLink({ place, button = false, tiny = false }) {
  const [open, setOpen] = useState(false)
  if (!place) return null
  const className = tiny ? 'hold-way hold-way--tiny' : button ? 'hold-action hold-way' : 'hold-map'

  const openMaps = (provider) => {
    window.open(mapsUrl(place, provider), '_blank', 'noopener,noreferrer')
    setOpen(false)
  }

  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        {button || tiny ? 'Find vej' : 'Kort'}
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={mapsDialogPaper}
        BackdropProps={mapsDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Find vej</DialogTitle>
        <DialogContent>
          <p className="hold-copy">Vælg hvilken kortapp der skal åbne.</p>
          <div className="hold-match-actions">
            <button type="button" className="hold-action" onClick={() => openMaps('apple')}>
              Apple Kort
            </button>
            <button type="button" className="hold-action" onClick={() => openMaps('google')}>
              Google Maps
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function People({ ids, nameOf, empty }) {
  if (!ids.length) return <p className="hold-empty">{empty}</p>
  return (
    <ul className="hold-people">
      {ids.map((id) => <li key={id}>{nameOf(id)}</li>)}
    </ul>
  )
}

export function SignupButton({
  item,
  season,
  onToggle,
  onDecline,
  declineLabel = 'Kan ikke spille',
  declinedText = 'Du har meldt, at du ikke kan spille',
}) {
  const meId = useTeamState()?.meId
  const started = hasStarted(item.kickoff)
  const cancelled = item.status === 'cancelled'
  const locked = !season.current || started || cancelled
  const attending = item.attendees.includes(meId)
  const waiting = item.waitlist.includes(meId)
  const declined = (item.declined || []).includes(meId)
  const full = item.max != null && item.attendees.length >= item.max

  if (cancelled) return <p className="hold-status hold-status--warn">Aflyst</p>
  if (!season.current) return null
  if (started) return <p className="hold-status">Tilmelding er lukket</p>

  let label = 'Meld mig til'
  let tone = 'hold-action--save'
  if (declined && onDecline) {
    label = 'Meld mig til alligevel'
    tone = 'hold-action--save'
  } else if (attending) {
    label = 'Meld mig fra'
    tone = 'hold-action--leave'
  } else if (waiting) {
    label = 'Forlad ventelisten'
    tone = ''
  } else if (full) {
    label = 'Sæt mig på venteliste'
    tone = ''
  }

  const primary = (
    <button
      type="button"
      className={`hold-action${tone ? ` ${tone}` : ''}`}
      disabled={locked && !attending && !waiting && !declined}
      onClick={onToggle}
    >
      {label}
    </button>
  )

  if (!onDecline) return primary

  if (declined) {
    return (
      <>
        {primary}
        <p className="hold-status hold-status--warn">{declinedText}</p>
      </>
    )
  }

  return (
    <div className="hold-match-actions">
      {primary}
      <button type="button" className="hold-action hold-action--ghost" disabled={locked} onClick={onDecline}>
        {declineLabel}
      </button>
    </div>
  )
}

export function SpotLine({ item }) {
  const meId = useTeamState()?.meId
  if (item.status === 'cancelled') return <span className="hold-pill hold-pill--muted">Aflyst</span>
  const waiting = item.waitlist.includes(meId)
  const attending = item.attendees.includes(meId)
  const declined = (item.declined || []).includes(meId)
  if (attending) return <span className="hold-pill">Meldt til</span>
  if (waiting) {
    const place = item.waitlist.indexOf(meId) + 1
    return <span className="hold-pill hold-pill--wait">Venteliste {place}</span>
  }
  if (declined) return <span className="hold-pill hold-pill--out">Kan ikke</span>
  if (item.max == null) return <span className="hold-pill hold-pill--muted">{item.attendees.length} tilmeldt</span>
  const left = Math.max(item.max - item.attendees.length, 0)
  return <span className="hold-pill hold-pill--muted">{left === 0 ? 'Fyldt' : `${left} pladser`}</span>
}

export function When({ iso }) {
  return <time dateTime={iso}>{formatKickoff(iso)}</time>
}

function dateTimeFace(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const text = formatKickoff(value)
  return text.charAt(0).toLocaleUpperCase('da') + text.slice(1)
}

export function DateTimeField({ type = 'datetime-local', value, onChange, suggest = '', required = false }) {
  const fieldRef = useRef(null)
  const face = type === 'datetime-local' ? dateTimeFace(value) : ''
  const openPicker = () => {
    const field = fieldRef.current
    if (!field) return
    if (!value && suggest) {
      field.value = suggest
      onChange({ target: { value: suggest } })
    }
    if (!field.showPicker) return
    try {
      field.showPicker()
    } catch {
      // Vælgeren er allerede åben, eller browseren åbner den selv.
    }
  }

  return (
    <span className={`hold-datetime${face ? ' has-value' : ''}`}>
      <input
        ref={fieldRef}
        type={type}
        value={value}
        required={required}
        onChange={onChange}
        onClick={openPicker}
      />
      {face ? <span className="hold-datetime-face" aria-hidden="true">{face}</span> : null}
    </span>
  )
}

export function Composer({ placeholder, onSubmit, allowImage = false }) {
  const [text, setText] = useState('')
  const [image, setImage] = useState(null)
  const fileRef = useRef(null)

  return (
    <form
      className="hold-composer"
      onSubmit={(event) => {
        event.preventDefault()
        if (!text.trim() && !image) return
        onSubmit(text, image)
        setText('')
        setImage(null)
      }}
    >
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={placeholder}
        rows={2}
      />
      {image ? (
        <div className="hold-photo-draft">
          <img src={image} alt="" />
          <button type="button" className="hold-text-button" onClick={() => setImage(null)}>Fjern billede</button>
        </div>
      ) : null}
      <div className="hold-row" style={{ justifyContent: 'flex-end' }}>
        {allowImage ? (
          <>
            <IconButton 
              className="hold-action hold-action--ghost hold-action--small" 
              icon={<FilterRoundedIcon fontSize="medium" />} 
              onClick={() => fileRef.current?.click()}
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={async (event) => {
                const file = event.target.files?.[0]
                event.target.value = ''
                if (!file) return
                try {
                  setImage(await readImage(file))
                } catch {
                  setImage(null)
                }
              }}
            />
          </>
        ) : null}
        <IconButton 
          className="hold-action hold-action--ghost hold-action--small" 
          icon={<SendRoundedIcon fontSize="medium" />} 
          onClick={() => onSubmit(text, image)}
          disabled={!text.trim() && !image}
          variant="outlined"
        />
      </div>
    </form>
  )
}

export function Heart({ filled }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 20.5s-7.2-4.6-9.6-8.6C.4 9.2 1.2 5.6 4.4 4.4c2.2-.8 4.3-.2 5.8 1.6L12 8.1l1.8-2.1c1.5-1.8 3.6-2.4 5.8-1.6 3.2 1.2 4 4.8 2 7.5-2.4 4-9.6 8.6-9.6 8.6z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function MeetingValue({ value }) {
  if (!value) return 'Ikke sat'
  if (/^\d{2}:\d{2}$/.test(value)) return value
  if (Number.isNaN(new Date(value).getTime())) return value
  return <When iso={value} />
}

export function MatchTimes({ match, meetingAlways = false }) {
  return (
    <dl className="hold-times">
      <dt>Kampstart</dt>
      <dd><When iso={match.kickoff} /></dd>
      {meetingAlways || match.meeting ? (
        <>
          <dt>Mødetid</dt>
          <dd><MeetingValue value={match.meeting} /></dd>
        </>
      ) : null}
    </dl>
  )
}

export function SquadNeed({ count, max, min = PREFERRED_SIGNUPS }) {
  const cap = Number.isInteger(max) && max > 0 ? max : null
  const short = min > 0 && count < min
  const label = cap
    ? `${count} af ${cap}${short ? ` · minimum ${min}` : ''}`
    : `${count} tilmeldt${short ? ` · minimum ${min}` : ''}`
  const width = cap ? `${Math.min(count / cap, 1) * 100}%` : '0%'
  const mark = cap && min > 0 && min < cap ? `${(min / cap) * 100}%` : null
  return (
    <span className={`hold-squad${short ? ' is-short' : ' is-ready'}`} aria-label={label}>
      {cap ? (
        <span className="hold-squad-track" aria-hidden="true">
          <span className="hold-squad-bar"><span className="hold-squad-fill" style={{ width }} /></span>
          {mark ? <span className="hold-squad-mark" style={{ left: mark }} /> : null}
        </span>
      ) : null}
      <span>{label}</span>
    </span>
  )
}

function listClock(iso) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return { time: '–', day: '' }
  return {
    time: new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date),
    day: new Intl.DateTimeFormat('da-DK', { day: 'numeric', month: 'short' }).format(date),
  }
}

function listMeeting(value) {
  if (!value) return ''
  if (/^\d{2}:\d{2}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date)
}

export function MatchLink({ teamId, match, matches = [] }) {
  const kick = listClock(match.kickoff)
  const meet = listMeeting(match.meeting)
  const cancelled = match.status === 'cancelled'
  const played = hasStarted(match.kickoff)
  return (
    <Link className={`hold-card hold-link-card hold-match${cancelled ? ' is-cancelled' : ''}${played ? ' is-played' : ''}`} to={`/hold/${teamId}/kamp/${match.id}`}>
      <span className="hold-match-bg" aria-hidden="true">
        <img src={matchBackdrop(matches, match)} alt="" />
      </span>
      <div className="hold-match-time">
        <strong>{kick.time}</strong>
        <em>{kick.day}</em>
      </div>
      <div className="hold-match-main">
        <div className="hold-card-top">
          <span className="hold-match-side">{match.home ? 'Hjemme' : 'Ude'}</span>
          <SpotLine item={match} />
        </div>
        <strong>{match.opponent}</strong>
        {meet ? <span className="hold-match-meet">Mødetid {meet}</span> : null}
        {match.place ? <span className="hold-meta">{match.place}</span> : null}
        {!played && !cancelled ? <SquadNeed count={match.attendees.length} max={match.max} /> : null}
      </div>
    </Link>
  )
}
